import { useEffect, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Cog, Loader2, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  platformSettingsErrorMessage,
  useGetPlatformSettings,
  useUpdatePlatformSettings,
} from "@/api/platform-settings";

/**
 * Platform settings — global, ADMIN-only substrate config.
 *
 * First section: the PAL operator-app install link. Operators get this URL in
 * their invite to install the app. It changes as the build target moves
 * (Diawi today, TestFlight / a store link later), so it lives in config and is
 * edited here rather than in a deploy.
 */

/** Empty input clears the setting; otherwise it must parse as a URL. */
function isValidUrl(value: string): boolean {
  const trimmed = value.trim();
  if (trimmed === "") return true;
  return URL.canParse(trimmed);
}

export function PlatformSettingsScreen() {
  const settingsQuery = useGetPlatformSettings();

  // Draft is a string; the stored value is `string | null`. Normalising null →
  // "" means an empty box and a cleared setting compare equal (not dirty).
  const stored = settingsQuery.data?.palInstallUrl ?? "";
  const [palInstallUrl, setPalInstallUrl] = useState("");

  // Reseed the draft whenever the stored value changes (initial load + after a
  // save invalidates the query). Guarded so a refetch never clobbers an edit in
  // progress — only reseed when the box still matches what we last loaded.
  const [seededFrom, setSeededFrom] = useState<string | null>(null);
  useEffect(() => {
    if (!settingsQuery.isSuccess) return;
    if (seededFrom === null || palInstallUrl === seededFrom) {
      setPalInstallUrl(stored);
      setSeededFrom(stored);
    }
  }, [settingsQuery.isSuccess, stored, seededFrom, palInstallUrl]);

  const updateMutation = useUpdatePlatformSettings({
    onSuccess: () => toast.success("Platform settings saved"),
    onError: (err) => toast.error(platformSettingsErrorMessage(err)),
  });

  const trimmed = palInstallUrl.trim();
  const valid = isValidUrl(palInstallUrl);
  const isDirty = trimmed !== stored.trim();
  const canSave = isDirty && valid && !updateMutation.isPending;

  const handleSave = () => {
    if (!canSave) return;
    updateMutation.mutate({ palInstallUrl: trimmed === "" ? null : trimmed });
  };

  if (settingsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (settingsQuery.isError) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 p-8 text-sm">
        <p className="text-destructive">Couldn't load platform settings</p>
        <p className="text-muted-foreground">
          {platformSettingsErrorMessage(settingsQuery.error)}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void settingsQuery.refetch()}
        >
          Try again
        </Button>
      </div>
    );
  }

  const updatedAt = settingsQuery.data?.updatedAt;

  return (
    <div className="space-y-6 p-4 lg:p-6">
      <div className="space-y-1">
        <h1 className="flex items-center gap-2 text-2xl font-semibold">
          <Cog className="h-6 w-6" />
          Platform settings
        </h1>
        <p className="text-sm text-muted-foreground">
          Global, EZX-managed configuration.
        </p>
      </div>

      <Card className="max-w-2xl">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="h-5 w-5" />
            PAL operator app
          </CardTitle>
          <CardDescription>
            The install link operators receive in their invite to set up the PAL
            app (a Diawi build today, TestFlight or a store link later).
            Changing it here takes effect without a deploy.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="pal-install-url">Operator app install link</Label>
            <Input
              id="pal-install-url"
              type="url"
              inputMode="url"
              placeholder="https://i.diawi.com/…"
              value={palInstallUrl}
              onChange={(e) => setPalInstallUrl(e.target.value)}
              aria-invalid={!valid}
            />
            {!valid ? (
              <p className="text-sm text-destructive">
                Enter a valid URL, or leave it empty to clear the link.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Leave empty to clear the link.
              </p>
            )}
          </div>

          <div className="flex items-center justify-between gap-4">
            <p className="text-xs text-muted-foreground">
              {updatedAt
                ? `Last updated ${format(new Date(updatedAt), "PP 'at' p")}`
                : "Not set yet"}
            </p>
            <Button onClick={handleSave} disabled={!canSave}>
              {updateMutation.isPending ? (
                <>
                  <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  Saving…
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
